import { createContext, useContext, useState, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface Property {
  id: string;
  name: string;
}

interface PropertyContextType {
  selectedProperty: Property | null;
  setSelectedProperty: (property: Property | null) => void;
  getPropertyUrl: (path: string) => string;
}

const PropertyContext = createContext<PropertyContextType | undefined>(undefined);

export const PropertyProvider = ({ children }: { children: ReactNode }) => {
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const getPropertyUrl = (path: string) => {
    if (!selectedProperty) {
      return path;
    }
    
    // Replace :projectId with the actual property ID
    return path.replace(':projectId', selectedProperty.id);
  };

  // Handle property selection changes
  const handlePropertySelection = (property: Property | null) => {
    setSelectedProperty(property);
    
    // If we're on a property-specific route, update the URL
    if (property && location.pathname.includes('/property-manager/')) {
      // Extract the current path segments
      const pathSegments = location.pathname.split('/');
      
      // Check if we're on a property-specific route (like /asbuilds)
      if (pathSegments.length >= 4 && pathSegments[3] === 'asbuilds') {
        // Construct the new URL with the new property ID
        const newPath = `/property-manager/${property.id}/asbuilds`;
        // Use navigate with replace to avoid adding to history
        navigate(newPath, { replace: true });
      }
    }
  };

  return (
    <PropertyContext.Provider value={{ 
      selectedProperty, 
      setSelectedProperty: handlePropertySelection, 
      getPropertyUrl 
    }}>
      {children}
    </PropertyContext.Provider>
  );
};

export const useProperty = () => {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error('useProperty must be used within a PropertyProvider');
  }
  return context;
}; 